<template>
  <b-card-body
    :class="{'rounded-bottom border shadow p-4 overflow-hidden': isPaymentMethodPage}"
    class="new-payment-method card-body "
  >
    <spinner v-if="busy" />
    <div v-else>
      <validation-observer ref="observer">
        <h2 class="h5">
          {{ $t('Credit / Debit Card') }}
        </h2>
        <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
          <b-form-group :label="$t('Cardholder name')">
            <b-form-input v-model="cardholderName" :state="(dirty || validated) ? (valid ? null : false) : null" />
            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
              {{ errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>
        <b-form-checkbox
          id="isDefault"
          v-model="isDefault"
          class="pb-2"
          name="isDefault"
          :value="true"
          :unchecked-value="false"
        >
          {{ $t('Add as default card?') }}
        </b-form-checkbox>
        <b-form-group
          :label="$t('Card number')"
          :state="cardValidationMessage == null"
          :invalid-feedback="cardValidationMessage"
        >
          <div id="card-element" class="form-control" />
        </b-form-group>

        <div class="row">
          <div class="col-12 col-sm-6 col-xl-12 col-xxl-6">
            <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
              <b-form-group :label="$t('Address line 1')">
                <b-form-input v-model="address1" :state="(dirty || validated) ? (valid ? null : false) : null" />
                <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>
          <div class="col-12 col-sm-6 col-xl-12 col-xxl-6">
            <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
              <b-form-group :label="$t('Town/City')">
                <b-form-input v-model="town" :state="(dirty || validated) ? (valid ? null : false) : null" />
                <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-sm-6 col-xl-12 col-xxl-6">
            <b-form-group :label="$t('Address line 2')">
              <b-form-input v-model="address2" />
            </b-form-group>
          </div>
          <div class="col-12 col-sm-6 col-xl-12 col-xxl-6">
            <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
              <b-form-group :label="$t('Postcode')">
                <b-form-input v-model="postcode" :state="(dirty || validated) ? (valid ? null : false) : null" />
                <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>
        </div>
        <b-button v-if="isCardSetup" variant="primary" size="sm" :disabled="secretKey==null || isSaving" @click="submit()">
          {{ $t('Save Payment Method') }}
        </b-button>
      </validation-observer>
    </div>

    <div v-if="isCardSetup" class="mt-3 pt-2">
      <div class="note small font-weight-normal">
        <i18n path="termsAndConditions" tag="p">
          <template v-slot:termsAndConditionsLink>
            <router-link class="text-link" :to="{name: 'tnc'}" target="_blank">
              {{ $t('termsAndConditionsLink') }}
            </router-link>
          </template>
        </i18n>
      </div>
      <p class="small font-weight-normal">
        <em>{{ $t('Leads.io Ltd is a registered data controller under the Data Protection Act 1998 number PZ216853X.') }}</em>
      </p>
      <div class="d-flex border-top pt-3 align-items-center justify-content-center flex-column">
        <img class="img-fluid payment__cards" src="/images/cards.jpg" alt="Cards">
        <img class="img-fluid mt-2" src="/images/powered_by_stripe.png" alt="Stripe">
      </div>
    </div>
  </b-card-body>
</template>

<script>
import Spinner from "@/components/animation/Spinner";
import {ValidationProvider, ValidationObserver} from 'vee-validate';
import company from "@/imports/mixins/company";
import stripeKey from "@/imports/mixins/stripeKey";
import {loadStripe} from '@stripe/stripe-js';

export default {
  name: "NewPaymentMethod",
  components: {ValidationProvider, ValidationObserver, Spinner},
  mixins: [stripeKey, company],
  props: {
    'isCardSetup': Boolean,
    'isPaymentMethodPage': Boolean
  },
  data: function() {
    return {
      cardholderName: null,
      address1: null,
      address2: null,
      town: null,
      postcode: null,
      cardElement: null,
      cardValidationMessage: null,
      stripe: null,
      secretKey: null,
      isDefault: false,
      busy: true,
      isSaving: false,
    };
  },
  methods: {
    submit: async function() {
      this.$refs.observer.validate().then(success => {
        if (!success) {
          return;
        }

        const paymentMethod = {
          type: 'card',
          card: this.cardElement,
          billing_details: {
            name: this.cardholderName,
            address: {
              country: 'GB',
              line1: this.address1,
              line2: this.address2,
              city: this.town,
              postal_code: this.postcode,
            }
          }
        };

        this.saveCard(paymentMethod);
      });
    },

    createPaymentMethod(paymentMethod) {
      this.cardValidationMessage = null;
      this.isSaving = true;
      this.stripe.createPaymentMethod(paymentMethod).then((response) => {
        if (response.error !== undefined) {
          this.cardValidationMessage = response.error.message;
        } else {
          this.$emit('created', response.paymentMethod.id);
        }
      }).finally(() => this.isSaving = false);
    },

    saveCard: function(payment_method) {
      this.isSaving = true;
      this.cardValidationMessage = null;

      this.stripe.confirmCardSetup(this.secretKey, {payment_method})
        .then((response) => {
          if (response.error !== undefined) {
            this.cardValidationMessage = response.error.message;
          } else {
            this.$api.payments.saveCard(response.setupIntent.id, this.isDefault)
              .then((data) => {
                if (data.errors === undefined) {
                  this.$emit('created', data.meta?.paymentMethod);
                }
              });
          }
        })
        .catch(error => {
          this.cardValidationMessage = error.message;
        })
        .finally(() => this.isSaving = false);
    }
  },
  created() {
    this.busy = true;
    Promise.all([
      this.$api.payments.createSetupIntent(),
      loadStripe(this.stripeKey())
    ]).then(([{data}, stripe]) => {
      this.busy = false; // Important, if busy is not cleared here then nextTick will fire before the finally call and the card element wont be ready
      this.cardholderName = data.data.name;
      this.address1 = data.data.address1;
      this.address2 = data.data.address2;
      this.town = data.data.town;
      this.postcode = data.data.postcode;
      this.secretKey = data.data.secret;

      this.$nextTick(() => {
        const elements = stripe.elements();
        this.cardElement = elements.create('card', {
          "style": {
            base: {
              color: '#013652',
              fontWeight: 300,
              fontSize: '14px',
            },
          },
          'hidePostalCode': true
        });
        this.cardElement.mount('#card-element');
        this.stripe = stripe;
        this.$emit('loaded');
      })
    }).finally(() => {
      this.busy = false
    });
  }
}
</script>

<style scoped lang="scss">

.payment__cards {
  max-width: 230px;
}

.invalid-feedback {
  color: #EB1C26;
}

::v-deep #card-element {
  @include media-breakpoint-up(md) {
    padding: .525rem .75rem;
  }
  @include media-breakpoint-down(sm) {
    padding: .425rem .75rem;
  }

  iframe {
    height: 33px !important;
  }
}

</style>
